import { useState, useCallback, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, cn } from "@tellescope/ui";

export interface DTMFDialpadProps {
  /** Callback when digits are sent */
  onSendDigits: (digits: string) => void;
  /** Whether the dialpad is disabled */
  disabled?: boolean;
  /** Additional className for the container */
  className?: string;
}

/**
 * DTMFDialpad - Inline input for sending DTMF tones during a call.
 *
 * Provides a text input field for entering digits with a Send button.
 * Validates input to only allow digits 0-9, *, #, and w.
 *
 * @example
 * ```tsx
 * <DTMFDialpad
 *   onSendDigits={handleSendDigits}
 *   disabled={callStatus !== 'open'}
 * />
 * ```
 */
export function DTMFDialpad({ onSendDigits, disabled = false, className }: DTMFDialpadProps) {
  const { t } = useTranslation("calling");
  const [digits, setDigits] = useState("");

  const handleSend = useCallback(() => {
    if (!digits.trim()) return;
    onSendDigits(digits);
    setDigits("");
  }, [digits, onSendDigits]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Filter input to only allow valid DTMF characters
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9#*w]/gi, "");
    setDigits(value);
  }, []);

  return (
    <div className={cn("flex items-center gap-2 px-4 py-3", className)}>
      <Input
        value={digits}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={t("Enter digits (e.g. 12345)")}
        disabled={disabled}
        className="flex-1 h-9"
      />
      <Button variant="ghost" size="sm" onClick={handleSend} disabled={disabled || !digits.trim()} className="shrink-0">
        {t("Send")}
      </Button>
    </div>
  );
}
